import type { SeoConfigType } from "~/lib/types";

type SiteId = "HSASTORE" | "FSASTORE" | "WDH";

// Site URLs (`defaultUrl`, `defaultOg.url`, `organization.url`) MUST be each
// site's CANONICAL production host — the apex for FSA/HSA, www for WDH — in
// lockstep with `CANONICAL_HOST_REDIRECTS` (cloudflare/canonical-host.ts) and
// `[env.production.vars]` SFRA_ORIGIN_*. They are consumed request-
// independently (Organization JSON-LD `url` in seo.home.server.ts, and the
// page `url` / `og:url` fallback in seo.server.ts / seo.home.server.ts), so a
// `www` value here would name a host that 301s away (COM-591) while the
// self-canonical (`app/seo/utils/site-url.ts`, built from the request origin)
// says apex — mixed signals to crawlers. Image / logo URLs deliberately stay
// on `www`: it is the production IMAGE host (`IMAGE_HOSTS` in
// app/config/environments.ts) and sub-resources are never redirected.
const CONFIGS: Record<SiteId, SeoConfigType> = {
  FSASTORE: {
    defaultTitle: "FSA Store",
    defaultTitleTemplate: "%s | FSA Store",
    defaultDescription:
      "Shop FSA-eligible products. The nation's largest assortment of flexible spending account eligible items.",
    defaultKeywords: "FSA store, flexible spending account, FSA eligible",
    defaultImage: "https://www.fsastore.com/images/Share-logo.jpg", // TODO: confirm CDN-served OG image path
    defaultUrl: "https://fsastore.com/",
    defaultH1: "FSA Store",
    defaultOg: {
      title: "FSA Store",
      description:
        "Shop FSA-eligible products. The nation's largest assortment of flexible spending account eligible items.",
      url: "https://fsastore.com/",
      type: "website",
    },
    organization: {
      legalname: "FSA Store", // TODO: confirm legal entity name from brand guidelines
      brand: "FSA Store",
      logo: "https://www.fsastore.com/images/Share-logo.jpg", // TODO: confirm CDN logo URL
      description:
        "FSA Store is the nation's largest online marketplace for FSA-eligible products.",
      url: "https://fsastore.com/",
    },
    sameAs: [], // TODO: add social handles from BM site prefs
    contact: [], // TODO: add contact info from BM site prefs
  },
  HSASTORE: {
    defaultTitle: "HSA Store",
    defaultTitleTemplate: "%s | HSA Store",
    defaultDescription:
      "Shop HSA-eligible products. The nation's largest assortment of health savings account eligible items.",
    defaultKeywords: "HSA store, health savings account, HSA eligible",
    defaultImage: "https://www.hsastore.com/images/Share-logo.jpg", // TODO: confirm CDN-served OG image path
    defaultUrl: "https://hsastore.com/",
    defaultH1: "HSA Store",
    defaultOg: {
      title: "HSA Store",
      description:
        "Shop HSA-eligible products. The nation's largest assortment of health savings account eligible items.",
      url: "https://hsastore.com/",
      type: "website",
    },
    organization: {
      legalname: "HSA Store", // TODO: confirm legal entity name
      brand: "HSA Store",
      logo: "https://www.hsastore.com/images/Share-logo.jpg", // TODO: confirm CDN logo URL
      description:
        "HSA Store is the nation's largest online marketplace for HSA-eligible products.",
      url: "https://hsastore.com/",
    },
    sameAs: [], // TODO: add social handles from BM site prefs
    contact: [], // TODO: add contact info from BM site prefs
  },
  WDH: {
    defaultTitle: "Well Deserved Health",
    defaultTitleTemplate: "%s | Well Deserved Health",
    defaultDescription:
      "Shop wellness and health products at Well Deserved Health.",
    defaultKeywords: "well deserved health, wellness products, health",
    defaultImage: "https://www.welldeservedhealth.com/images/Share-logo.jpg", // TODO: confirm CDN-served OG image path
    defaultUrl: "https://www.welldeservedhealth.com/",
    defaultH1: "Well Deserved Health",
    defaultOg: {
      title: "Well Deserved Health",
      description: "Shop wellness and health products at Well Deserved Health.",
      url: "https://www.welldeservedhealth.com/",
      type: "website",
    },
    organization: {
      legalname: "Well Deserved Health", // TODO: confirm legal entity name
      brand: "Well Deserved Health",
      logo: "https://www.welldeservedhealth.com/images/Share-logo.jpg", // TODO: confirm CDN logo URL
      description:
        "Well Deserved Health offers quality wellness and health products.",
      url: "https://www.welldeservedhealth.com/",
    },
    sameAs: [], // TODO: add social handles from BM site prefs
    contact: [], // TODO: add contact info from BM site prefs
  },
};

export function getSeoConfig(siteId: string): SeoConfigType {
  const config = CONFIGS[siteId as SiteId];
  if (!config) {
    console.warn(
      `[seo.config] Unknown siteId "${siteId}", falling back to FSASTORE`,
    );
    return CONFIGS.FSASTORE;
  }
  return config;
}
